import { ChatMessage } from "@/services/promptEditor.service";
import { Prompt, PromptKernelRequest, PromptResponse } from "./prompt";
import { Tool, ToolLogInput, ToolResponse, ToolIdentifiers } from "./tool";
import { Evaluator, EvaluatorResponse, EvaluatorSpec } from "./evaluator";
import { parseFileResponse } from "./file";
import { Dayjs } from "dayjs";
import { parseTimestamp } from "@/services/utils";
import { Flow, FlowResponse, parseFlowResponse } from "./flow";
import { EvaluationDatapoint, EvaluationRun } from "@/services/evaluationReports.service";
import { getLatestEvaluatorLog } from "@/services/evaluators.service";
import { Filter } from "../../lib/filtering/filters";
import { Agent, AgentKernelRequest, AgentResponse } from "./agent";
import { ChatRole } from "humanloop/api/types/ChatRole";

export interface ToolChoice {
  type: string;
  function: Record<string, any>;
}

export type ToolChoiceOptions = "none" | "auto" | "required" | ToolChoice | null;

export type ObservabilityStatus = "pending" | "running" | "completed" | "failed";

/**
 * Base timestamps for any generations/logs request.
 */
export interface LogRequestTimeStamps {
  /**
   * When the logged event started.
   */
  start_time?: string | null;

  /**
   * When the logged event ended.
   */
  end_time?: string | null;
}

// Responses always have timestamps.
export interface LogResponseTimeStamps extends LogRequestTimeStamps {
  start_time: string | null;
  end_time: string | null;
}

interface BaseDatumRequest extends LogRequestTimeStamps {
  session_id: string | null;
  parent_id: string | null;
  inputs: Record<string, any> | null;
  source: string | null;
  metadata: Record<string, any> | null;
  save: boolean;
  source_datapoint_id: string | null;
  batches: string[] | null;
  user: string | null;
  environment: string | null;
  log_status: "complete" | "incomplete" | null;
}

interface BaseLogRequestInput {
  provider_request: Record<string, any> | null;
  provider_response: Record<string, any> | null;
}

interface BaseLogRequestOutput {
  output: string | null;
  raw_output: string | null;
  created_at: string | null;
  error: string | null;
  stdout: string | null;
  provider_latency: number | null;
}

export interface LogRequest extends BaseDatumRequest, BaseLogRequestInput, BaseLogRequestOutput {}

export interface BaseLogResponse extends Omit<LogRequest, "start_time" | "end_time">, LogResponseTimeStamps {
  id: string;
  observability_status: ObservabilityStatus;
  updated_at: string;
  evaluator_logs: EvaluatorLogResponse[];
  trace_id: string | null;
  trace_flow_id: string | null;
  trace_parent_id: string | null;
}

// For v5 we have a different response type for creating logs compared to the actual log response.
// Ideally this would be updated to be consistent.
export interface CreateLogResponse {
  id: string;
  parent_id: string;
  session_id: string | null;
  version_id: string;
}

export type FilterOperator = "eq" | "ne" | "co" | "nc" | "gt" | "lt" | "er" | "ok";

export const filterOperatorCodeToName: Record<FilterOperator, string> = {
  eq: "equals",
  ne: "not equals",
  co: "contains",
  nc: "not contains",
  gt: "greater than",
  lt: "less than",
  er: "error",
  ok: "no error",
};

interface BasePromptLogInput {
  messages: ChatMessage[] | null;
  tool_choice: ToolChoiceOptions;
}

interface PromptLogInput extends BasePromptLogInput {
  prompt: PromptKernelRequest | null;
}

interface BasePromptLogOutput {
  output_message: ChatMessage | null;
  prompt_tokens: number | null;
  output_tokens: number | null;
  prompt_cost: number | null;
  output_cost: number | null;
  finish_reason: string | null;
}

// This is PromptRequestIdentifiers, ToolRequestIdentifiers, etc. in the backend.
// While they're separate classes in the backend as we want to have specific documentation strings,
// we don't have that requirement here.
export interface FileRequestIdentifiers {
  path?: string | null;
  id?: string | null;
  directory_id?: string | null;
}

interface PromptLogRequest
  extends LogRequest,
    PromptLogInput,
    BasePromptLogOutput,
    FileRequestIdentifiers,
    LogRequestTimeStamps {}

export interface PromptLogResponse
  extends BaseLogResponse,
    Omit<PromptLogRequest, "path" | "id" | "prompt" | "start_time" | "end_time"> {
  prompt: PromptResponse;
  trace_children: LogResponse[];
}

interface AgentLogRequest extends LogRequest, AgentKernelRequest, BasePromptLogOutput, FileRequestIdentifiers {}

export interface AgentLogResponse
  extends BaseLogResponse,
    Omit<AgentLogRequest, "path" | "id" | "agent" | "start_time" | "end_time"> {
  agent: AgentResponse;
  messages: ChatMessage[] | null;
  trace_children: LogResponse[];
}

/**
 * Request for creating a Tool log.
 */
interface ToolLogRequest extends LogRequest, ToolLogInput, ToolIdentifiers {
  trace_children?: LogResponse[];
}

export interface ToolLogResponse
  extends BaseLogResponse,
    Omit<ToolLogRequest, "id" | "path" | "tool" | "start_time" | "end_time"> {
  tool: ToolResponse;
  trace_children: LogResponse[];
}

interface EvaluatorLogRequest extends LogRequestTimeStamps {
  judgment: any;
  marked_completed?: boolean;
}

export interface CreateEvaluatorLogRequest extends EvaluatorLogRequest, FileRequestIdentifiers, LogRequestTimeStamps {
  parent_id: string;
  spec?: EvaluatorSpec;
  user?: string;
}

export interface EvaluatorLogResponse
  extends BaseLogResponse,
    Omit<CreateEvaluatorLogRequest, "id" | "path" | "parent_id" | "user" | "start_time" | "end_time"> {
  evaluator: EvaluatorResponse;
  parent: LogResponse | null;
  // parent_id can be null in the response, but it's required in the request.
  // We have some legacy logs that don't have a parent_id
  parent_id: string | null;
  trace_children: LogResponse[];
}

export interface FlowLogResponse extends BaseLogResponse {
  flow: FlowResponse;
  trace_children: LogResponse[];
  log_status: "complete" | "incomplete" | null;
}

export type LogResponse =
  | PromptLogResponse
  | ToolLogResponse
  | EvaluatorLogResponse
  | FlowLogResponse
  | AgentLogResponse;

export type LogFromResponse<T> = Omit<
  T,
  | "prompt"
  | "tool"
  | "agent"
  | "evaluator"
  | "flow"
  | "created_at"
  | "updated_at"
  | "evaluator_logs"
  | "parent"
  | "start_time"
  | "end_time"
  | "trace_children"
> & {
  created_at: Dayjs;
  updated_at: Dayjs;
  start_time: Dayjs | null;
  end_time: Dayjs | null;
  // We can't add this here as it would create a circular dependency.
  // We have to add it individually to each Log type below.
  // evaluator_logs: EvaluatorLog[];
};

export interface AgentLog extends LogFromResponse<AgentLogResponse> {
  agent: Agent;
  evaluator_logs: EvaluatorLog[];
  trace_children: Log[];
}

export type PromptLog = LogFromResponse<PromptLogResponse> & { prompt: Prompt } & { evaluator_logs: EvaluatorLog[] } & {
  trace_children: Log[];
};
export type ToolLog = LogFromResponse<ToolLogResponse> & { tool: Tool } & { evaluator_logs: EvaluatorLog[] } & {
  trace_children: Log[];
};
export type EvaluatorLog = LogFromResponse<EvaluatorLogResponse> & { evaluator: Evaluator } & {
  evaluator_logs: EvaluatorLog[];
  parent: Log | null;
} & { trace_children: Log[] };
export type FlowLog = LogFromResponse<FlowLogResponse> & { flow: Flow; evaluator_logs: EvaluatorLog[] } & {
  trace_children: Log[];
};

export type Log = AgentLog | PromptLog | ToolLog | EvaluatorLog | FlowLog;

export const parseLogResponse = (response: LogResponse): Log => {
  if (!response.created_at) {
    console.log("Log response is missing created_at", response);
    throw new Error(`Log response is missing created_at: '${response.id}'`);
  }
  const parsedTimestamps = {
    created_at: parseTimestamp(response.created_at),
    updated_at: parseTimestamp(response.updated_at),
    start_time: response.start_time !== null ? parseTimestamp(response.start_time) : null,
    end_time: response.end_time !== null ? parseTimestamp(response.end_time) : null,
  };
  const parsedEvaluatorLogs: EvaluatorLog[] = response.evaluator_logs.map(parseLogResponse) as EvaluatorLog[];

  if ("prompt" in response) {
    return {
      ...response,
      ...parsedTimestamps,
      prompt: parseFileResponse(response.prompt),
      evaluator_logs: parsedEvaluatorLogs,
      trace_children: response.trace_children.map(parseLogResponse),
    };
  } else if ("agent" in response) {
    return {
      ...response,
      ...parsedTimestamps,
      agent: parseFileResponse(response.agent),
      evaluator_logs: parsedEvaluatorLogs,
      trace_children: response.trace_children.map(parseLogResponse),
    };
  } else if ("tool" in response) {
    return {
      ...response,
      ...parsedTimestamps,
      tool: parseFileResponse(response.tool),
      evaluator_logs: parsedEvaluatorLogs,
      trace_children: response.trace_children.map(parseLogResponse),
    };
  } else if ("evaluator" in response) {
    return {
      ...response,
      ...parsedTimestamps,
      evaluator: parseFileResponse(response.evaluator),
      evaluator_logs: parsedEvaluatorLogs,
      parent: response.parent ? (parseLogResponse(response.parent) as Log) : null,
      trace_children: response.trace_children.map(parseLogResponse),
    };
  } else if ("flow" in response) {
    return {
      ...response,
      ...parsedTimestamps,
      flow: parseFlowResponse(response.flow),
      evaluator_logs: parsedEvaluatorLogs,
      trace_children: response.trace_children.map(parseLogResponse),
    };
  }
  let x: never = response;
  throw new Error(`Unhandled response type: ${x}`);
};

export const getFileFromLog = (log: Log): Prompt | Tool | Evaluator | Flow | Agent => {
  if ("prompt" in log) {
    return log.prompt;
  } else if ("tool" in log) {
    return log.tool;
  } else if ("evaluator" in log) {
    return log.evaluator;
  } else if ("flow" in log) {
    return log.flow;
  } else if ("agent" in log) {
    return log.agent;
  }
  let x: never = log;
  throw new Error(`Unhandled log type: ${x}`);
};

/** Get the Prompt from a Log.
 *
 * Retrieves the Prompt from a Prompt Log, or the Prompt from an AI Evaluator Log.
 * (Cannot handle linked tools here as the types are different.)
 */
export const getPromptFromLog = (log: Log): Omit<PromptKernelRequest, "linked_tools"> | undefined => {
  if ("prompt" in log) {
    return log.prompt;
  } else if ("evaluator" in log && "prompt" in log.evaluator.spec) {
    return log.evaluator.spec.prompt;
  }
  return undefined;
};

/**
 * Extracts the "reasoning" argument from a log's output message if it exists in a tool call's arguments.
 *
 * For our Evaluator Logs.
 * This looks for a single tool call in the log's output_message and checks if its
 * arguments contain a "reasoning" field. If found, returns that reasoning as a string.
 */
export const getReasoningFromLog = (log: any): string | null => {
  if (
    "output_message" in log &&
    log.output_message &&
    log.output_message.tool_calls &&
    log.output_message.tool_calls.length === 1
  ) {
    const toolCall = log.output_message.tool_calls[0];
    try {
      const parsedArguments = JSON.parse(toolCall.function.arguments);
      if ("reasoning" in parsedArguments) {
        return parsedArguments.reasoning;
      }
    } catch (e) {
      // Do nothing
    }
  }
  return null;
};

export const getLogMatchingRun = (datapoint: EvaluationDatapoint, run: EvaluationRun): Log | null => {
  //  Loop through datapoint logs and find log that corresponds to the given evaluatee
  const logsMatchingEvaluatee = datapoint.logs.filter((log) => log.run_id === run.id);
  if (logsMatchingEvaluatee.length > 1) {
    console.error(`Expected to find one match, found ${logsMatchingEvaluatee.length}`, datapoint, run);
    return null;
  }
  return logsMatchingEvaluatee.length > 0 ? logsMatchingEvaluatee[0].log : null;
};

// Note: we filter logs on the backend; however, we return all logs for the datapoint if at least one log matches the filter.
// We believe this is better for UX (not showing logs makes the table look broken).
// However, we want to fade logs that don't match the filter (but still show them).
// This leads to complexity as we need to have the exact same logic on the backend and frontend to ensure we match the same logs.
// Especially tricky as the numbers might have different precision, etc.
// Maybe there is a better way!
export const logSatisfiesFilters = (log: Log, filters: Filter[]): boolean => {
  const judgmentFilters = filters.filter((filter) => filter.target.entity === "evaluator" && filter.value !== null);
  if (judgmentFilters.length === 0) {
    return true;
  }

  const evaluator_version_id_to_filter: Record<string, Filter> = Object.fromEntries(
    judgmentFilters.map((filter) => [(filter.target.target as Evaluator).version_id, filter]),
  );

  return Object.entries(evaluator_version_id_to_filter).every(([version_id, filter]) => {
    const evaluatorLogs =
      log?.evaluator_logs?.filter((evaluatorLog) => evaluatorLog.evaluator.version_id === version_id) || [];

    const lastEvaluatorLog = getLatestEvaluatorLog(evaluatorLogs);

    // Handle cases where there's no evaluator log
    if (!lastEvaluatorLog) {
      return filter.operator.code === "nc" || filter.operator.code === "ne";
    }

    const judgment = lastEvaluatorLog.judgment;
    const value = filter.value;
    const returnType = lastEvaluatorLog.evaluator.spec.return_type;

    // Convert values based on return type
    let convertedJudgment = judgment;
    let convertedValue = value;

    if (!["er", "ok"].includes(filter.operator.code)) {
      switch (returnType) {
        case "number":
          convertedJudgment =
            typeof judgment === "number" || typeof judgment === "string" ? parseFloat(judgment as string) : null;
          convertedValue = parseFloat(value);
          break;
        case "boolean":
          convertedJudgment = judgment !== null ? judgment.toString().toLowerCase() === "true" : null;
          convertedValue = value.toString().toLowerCase() === "true";
          break;
        default:
          convertedJudgment = judgment ? String(judgment) : null;
          convertedValue = String(value);
      }
    }

    // Handle comparison based on operator
    switch (filter.operator.code) {
      case "eq":
        return convertedJudgment === convertedValue;
      case "ne":
        return convertedJudgment !== convertedValue;
      case "co":
        return typeof convertedJudgment === "string" && convertedJudgment.includes(convertedValue);
      case "nc":
        return typeof convertedJudgment !== "string" || !convertedJudgment.includes(convertedValue);
      case "gt":
        return (
          typeof convertedJudgment === "number" &&
          typeof convertedValue === "number" &&
          convertedJudgment > convertedValue
        );
      case "lt":
        return (
          typeof convertedJudgment === "number" &&
          typeof convertedValue === "number" &&
          convertedJudgment < convertedValue
        );
      case "er":
        return lastEvaluatorLog.error !== null;
      case "ok":
        return lastEvaluatorLog.error === null;
      default:
        return false;
    }
  });
};

export const getOutputMessagesFromTraceChildren = (trace_head: Log): ChatMessage[] => {
  const messages: ChatMessage[] = [];
  const stack: Log[] = [trace_head];

  while (stack.length > 0) {
    const current = stack.pop();

    if (!current) {
      continue;
    }

    if ("error" in current && current.error) {
      messages.push({
        role: "tool" in current ? "tool" : "assistant",
        name: "tool" in current ? current.tool.name : undefined,
        content: current.error,
      });
    }

    if ("output_message" in current && current.output_message) {
      messages.push(current.output_message);
    } else if ("tool" in current && current.output) {
      messages.push({
        role: "tool",
        name: current.tool.name,
        content: current.output,
      });
    }

    if ("trace_children" in current && Array.isArray(current.trace_children)) {
      // Push children in reverse order to maintain original order when processing
      for (let i = current.trace_children.length - 1; i >= 0; i--) {
        stack.push(current.trace_children[i]);
      }
    }
  }

  return messages;
};

interface AgentTrace {
  /** Logs produced by the agent */
  agentLogs: Log[];
  /** Tool logs (can be Prompt, Tool or another Agent Logs) generated by the agent calling a tool */
  toolLogs: Log[];
}

/*
Grab logs produced by an agent and all tool logs generated by the agent when calling a tool.
*/
export const parseAgentChildrentMessages = (agent_turn_log: Log): AgentTrace => {
  if (!("agent" in agent_turn_log)) {
    return {
      agentLogs: [],
      toolLogs: [],
    };
  }
  const agentId = agent_turn_log.agent.id;

  const agentLogs: Log[] = [];
  const toolLogs: Log[] = [];

  const stack: Log[] = [...agent_turn_log.trace_children];

  while (stack.length > 0) {
    stack.sort((a, b) => {
      if (a.created_at && b.created_at) {
        return a.created_at.diff(b.created_at);
      }
      return 0;
    });
    const current: Log | undefined = stack.shift();

    if (!current) {
      continue;
    }

    if ("agent" in current && current.agent.id === agentId) {
      agentLogs.push(current);
    } else {
      toolLogs.push(current);
    }

    if ("trace_children" in current && Array.isArray(current.trace_children)) {
      // Push children in reverse order to maintain original order when processing
      for (let i = current.trace_children.length - 1; i >= 0; i--) {
        stack.push(current.trace_children[i]);
      }
    }
  }

  return {
    agentLogs,
    toolLogs,
  };
};
